/**
 * lib/api/client.ts — Stage FW-1 (Frontend Wiring 1: Auth)
 *
 * Typed fetch wrapper that:
 * 1. Reads VITE_API_URL from env
 * 2. Injects Authorization: Bearer <access> on every request
 * 3. On 401: attempts refresh ONCE via /auth/token/refresh/
 * 4. Single-flight refresh: concurrent 401s share one refresh promise
 * 5. On refresh success: retries original request once
 * 6. On refresh failure: clears auth state + redirects to /login
 */

import { useAuthStore } from '@/lib/store/auth-slice';
import { logoutFromOkta } from '@/lib/okta';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/** Default request timeout in milliseconds */
const DEFAULT_TIMEOUT_MS = 30000;

/** Timeout error for identification */
export class TimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

/** Network error for identification */
export class NetworkError extends Error {
  constructor(message = 'Network error occurred') {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Create a fetch with timeout using AbortController
 */
function fetchWithTimeout(
  url: string,
  options: RequestInit & { timeout?: number }
): Promise<Response> {
  const { timeout = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  return fetch(url, {
    ...fetchOptions,
    signal: controller.signal,
  })
    .then((response) => {
      clearTimeout(timeoutId);
      return response;
    })
    .catch((error) => {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new TimeoutError(`Request timed out after ${timeout}ms`);
      }
      throw new NetworkError(error.message);
    });
}

/**
 * Single-flight refresh promise. Shared across concurrent 401s so we only
 * refresh once, not N times. Reset to null after refresh completes/fails.
 */
let refreshPromise: Promise<{ access: string; refresh: string }> | null = null;

/**
 * Perform token refresh via /auth/token/refresh/.
 * Relies on httpOnly cookie (set by backend on login). On reload, in-memory
 * tokens are empty, but the cookie persists → send empty body, backend reads
 * the refresh token from the cookie.
 * Returns new tokens or throws on failure.
 */
async function performRefresh(): Promise<{ access: string; refresh: string }> {
  const res = await fetch(`${API_BASE}/api/v1/auth/token/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error('Refresh failed');
  }

  const data = await res.json();
  useAuthStore.getState().updateTokens({ access: data.access, refresh: data.refresh });
  return { access: data.access, refresh: data.refresh };
}

/**
 * Single-flight refresh: if refresh is already in progress, return the
 * existing promise. Otherwise, start a new refresh.
 */
async function refreshTokens(): Promise<{ access: string; refresh: string }> {
  if (!refreshPromise) {
    refreshPromise = performRefresh()
      .then((tokens) => {
        // Update auth store with new tokens
        useAuthStore.getState().updateTokens(tokens);
        refreshPromise = null; // Reset
        return tokens;
      })
      .catch(async (err) => {
        refreshPromise = null; // Reset
        // On refresh failure, clear auth and redirect to login
        useAuthStore.getState().clearAuth();
        // End Okta IdP session if user logged in via SSO
        await logoutFromOkta();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        throw err;
      });
  }
  return refreshPromise;
}

/** Options for API requests */
export interface ApiOptions {
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Parse the body as JSON (default) or return it as a Blob (a file download). */
  responseType?: 'json' | 'blob';
}

/**
 * Generic fetch wrapper with auto Bearer injection, 401 refresh handling,
 * and configurable timeout.
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit & ApiOptions = {}
): Promise<T> {
  const { timeout = DEFAULT_TIMEOUT_MS, responseType = 'json', ...fetchOpts } = options;
  const { accessToken } = useAuthStore.getState();

  // Build headers
  const headers = new Headers(fetchOpts.headers);
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  if (!headers.has('Content-Type') && fetchOpts.body) {
    headers.set('Content-Type', 'application/json');
  }

  // First attempt with timeout
  let res = await fetchWithTimeout(`${API_BASE}${endpoint}`, {
    ...fetchOpts,
    headers,
    credentials: 'include',
    timeout,
  });

  // If 401, try refresh + retry ONCE
  if (res.status === 401) {
    try {
      await refreshTokens();
      // Retry with new access token
      const newAccessToken = useAuthStore.getState().accessToken;
      if (newAccessToken) {
        headers.set('Authorization', `Bearer ${newAccessToken}`);
      }
      res = await fetchWithTimeout(`${API_BASE}${endpoint}`, {
        ...fetchOpts,
        headers,
        credentials: 'include',
        timeout,
      });
    } catch (err) {
      // Refresh failed, already redirected to /login by refreshTokens()
      throw err;
    }
  }

  // Parse response
  if (!res.ok) {
    const errorBody = await res.text();
    throw new Error(
      `API error ${res.status}: ${errorBody || res.statusText}`
    );
  }

  // Handle empty 204 responses
  if (res.status === 204 || res.headers.get('Content-Length') === '0') {
    return {} as T;
  }

  return (responseType === 'blob' ? res.blob() : res.json()) as Promise<T>;
}

/**
 * Typed GET helper
 */
export async function get<T>(endpoint: string): Promise<T> {
  return apiFetch<T>(endpoint, { method: 'GET' });
}

/**
 * GET returning the body as a Blob (a file export: xlsx / csv)
 */
export async function getBlob(endpoint: string): Promise<Blob> {
  return apiFetch<Blob>(endpoint, { method: 'GET', responseType: 'blob' });
}

/**
 * Typed POST helper
 */
export async function post<T>(
  endpoint: string,
  body?: unknown
): Promise<T> {
  return apiFetch<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Typed PUT helper
 */
export async function put<T>(
  endpoint: string,
  body?: unknown
): Promise<T> {
  return apiFetch<T>(endpoint, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Typed PATCH helper
 */
export async function patch<T>(
  endpoint: string,
  body?: unknown
): Promise<T> {
  return apiFetch<T>(endpoint, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Typed DELETE helper
 */
export async function del<T>(endpoint: string): Promise<T> {
  return apiFetch<T>(endpoint, { method: 'DELETE' });
}

/**
 * Unauthenticated POST (for login)
 */
export async function postPublic<T>(
  endpoint: string,
  body?: unknown
): Promise<T> {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  if (!res.ok) {
    const errorBody = await res.text();
    throw new Error(
      `API error ${res.status}: ${errorBody || res.statusText}`
    );
  }

  return res.json();
}

/**
 * Logout function that clears the refresh cookie and blacklists the token.
 * Clears in-memory auth state regardless of API call success.
 * Also ends Okta IdP session for SSO users.
 */
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/v1/auth/logout/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
  } finally {
    // Clear in-memory tokens/user regardless of API success
    useAuthStore.getState().clearAuth();
    // End Okta IdP session if user logged in via SSO.
    // For non-Okta users, this is a no-op (getOktaAuth returns null).
    // Note: logoutFromOkta may redirect to Okta's logout endpoint.
    await logoutFromOkta();
  }
}

/**
 * Exported for AuthGuard to attempt cookie-based refresh on mount (step 3 of
 * httpOnly-cookie auth). Allows session restoration after page reload.
 */
export { refreshTokens };
